import { Fade } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { GENERAL__CLOSE, KIOSK__SELECTION_EXIT, KIOSK__SELECTION_RELOAD } from 'translations/constants';
import { ReactComponent as CloseIcon } from '../../../assets/icons/Close.svg';
import { ReactComponent as KioskLogoutIcon } from '../../../assets/icons/KioskLogoutIcon.svg';
import { ReactComponent as KioskRefreshIcon } from '../../../assets/icons/KioskRefreshIcon.svg';
import { KIOSK_STAGE_FADE_MS } from '../kiosk-stage';
import { KIOSK_SELECTION_TOKENS, KioskChromeButton, KioskHeaderActions, KioskHeaderRow } from '../styles';
import KioskLanguageSelector from './KioskLanguageSelector';

interface KioskHeaderBarProps {
  onReload: () => void;
  /** Only the feed needs the reload button — it fades out on every other stage. */
  reloadVisible: boolean;
  /** 'logout' = leave kiosk mode (feed); 'close' = back to the feed (every other stage). */
  variant: 'logout' | 'close';
  onAction: () => void;
  /** Disables the action button while the current stage has a PIN verify / submit in flight. */
  actionDisabled?: boolean;
}

/**
 * Persistent chrome, rendered once outside every per-stage transition so it never re-animates:
 * a reload button (left, feed-only), the language selector, and a single top-right button whose icon
 * and behavior swap between "leave kiosk mode" (feed) and "back to the feed" (every other stage).
 */
const KioskHeaderBar = ({ onReload, reloadVisible, variant, onAction, actionDisabled }: KioskHeaderBarProps) => {
  const [t] = useTranslation();
  const isClose = variant === 'close';

  return (
    <KioskHeaderRow>
      <Fade in={reloadVisible} timeout={KIOSK_STAGE_FADE_MS}>
        <KioskChromeButton
          onClick={onReload}
          disabled={!reloadVisible}
          aria-label={t(KIOSK__SELECTION_RELOAD)}
          id="kiosk-reload"
        >
          <KioskRefreshIcon />
        </KioskChromeButton>
      </Fade>

      <KioskHeaderActions>
        <KioskLanguageSelector />
        <KioskChromeButton
          onClick={onAction}
          disabled={actionDisabled}
          aria-label={t(isClose ? GENERAL__CLOSE : KIOSK__SELECTION_EXIT)}
          id={isClose ? 'kiosk-stage-close' : 'kiosk-exit'}
        >
          {isClose ? <CloseIcon fill={KIOSK_SELECTION_TOKENS.chromeIcon} /> : <KioskLogoutIcon />}
        </KioskChromeButton>
      </KioskHeaderActions>
    </KioskHeaderRow>
  );
};

KioskHeaderBar.defaultProps = {
  actionDisabled: false,
};

export default KioskHeaderBar;
